import { v4 as uuid } from 'uuid';

import { Id, Optional } from '@vimeo/core/types/generic';
import { TeamUser } from '@vimeo/viewer';

import {
  EComposerApplicationType,
  EComposerSessionType,
  TComposerSessionId,
} from '../types/composer_session';

/**
 * Interface representing the configuration for live tracking.
 *
 * If you are going to modify this file:
 * - Ask QA to test BP (Big Picture) events.
 * - Note that some fields may be required in the BP schema.
 */
export interface ILiveTrackingConfig {
  DETAILS: {
    USER_ID: Optional<Id>;
    USER_LOCALE: Optional<string>;
    SESSION_USER_ROLE: Optional<string>;
    SESSION_PAGE_UUID: string;
    SESSION_APPLICATION: Optional<EComposerApplicationType>;
    SESSION_TYPE: Optional<EComposerSessionType>;
    SESSION_ID: Optional<TComposerSessionId>;
    SESSION_CLOUD_ID: Optional<Id>;
  };
  BIG_PICTURE_LIVE_SCHEMA_BASE: {
    page: Optional<EComposerApplicationType>;
    /**
     * Status of live event.
     */
    liveStatus: Optional<string>;
    /**
     * Clip ID.
     */
    videoId: Optional<number>;
    /**
     * Session type/id.
     */
    liveEventId: Optional<Id>;
    liveEventType: Optional<EComposerSessionType>;
    // DO NOT REMOVE IT as this field is required in the BP schema.
    isTestEvent: boolean;
    /**
     * Source from which user have joined live page.
     */
    leadSource: Optional<string>;
  };
  BIG_PICTURE_INTERACTION_SCHEMA_BASE: {
    liveStatus: Optional<string>;
    page: Optional<EComposerApplicationType>;
    view: Optional<string>;
    videoId: Optional<number>;
    roomId: Optional<Id>;
    roomType: Optional<EComposerSessionType>;
  };
  BIG_PICTURE_SCHEME_ADDITIONAL: {
    isSimulive: Optional<boolean>;
    isRecord: Optional<boolean>;
  };
  BIG_PICTURE_TEAM_CONTEXT_BASE: {
    teamOwnerId: Optional<Id>;
    teamAccountType: Optional<string>;
    teamUser: Optional<TeamUser>;
  };
  BIG_PICTURE_EVENT_SETTINGS_SCHEMA_BASE: {
    eventPrivacy: Optional<string>;
    productionMethod: Optional<string>;
  };
  BIG_PICTURE_VENUE_SCHEMA_BASE: {
    venueId: string;
    ownerId: number;
    spaceId: string;
    registrantId: string;
    sessionId: string;
    isSpacePrivate: boolean;
    isSpaceBreakout: boolean;
    recordingType: Optional<boolean>;
  };
}

/**
 * Contains defaults shared among simple live analytics.
 * This object export is hydrated over lifecycle of page.
 */
export const liveTrackingConfig: ILiveTrackingConfig = {
  DETAILS: {
    SESSION_PAGE_UUID: uuid(),
    SESSION_APPLICATION: EComposerApplicationType.UNKNOWN,
    SESSION_TYPE: EComposerSessionType.UNKNOWN,
    SESSION_USER_ROLE: null,
    SESSION_ID: null,
    USER_ID: null,
    USER_LOCALE: null,
    SESSION_CLOUD_ID: null,
  },
  BIG_PICTURE_LIVE_SCHEMA_BASE: {
    page: null,
    liveStatus: null,
    videoId: null,
    liveEventId: null,
    liveEventType: null,
    // DO NOT REMOVE IT as this field is required in the BP schema.
    isTestEvent: false,
    leadSource: null,
  },
  BIG_PICTURE_INTERACTION_SCHEMA_BASE: {
    liveStatus: null,
    page: null,
    view: null,
    videoId: null,
    roomId: null,
    roomType: null,
  },
  BIG_PICTURE_SCHEME_ADDITIONAL: {
    isSimulive: null,
    isRecord: null,
  },
  BIG_PICTURE_TEAM_CONTEXT_BASE: {
    teamOwnerId: null,
    teamAccountType: null,
    teamUser: null,
  },
  BIG_PICTURE_EVENT_SETTINGS_SCHEMA_BASE: {
    eventPrivacy: null,
    productionMethod: null,
  },
  BIG_PICTURE_VENUE_SCHEMA_BASE: {} as ILiveTrackingConfig['BIG_PICTURE_VENUE_SCHEMA_BASE'],
};
